<template>
    <div class="alert-payload-details">
        <!-- Header -->
        <div class="d-flex align-center ga-3 pa-3 alert-header" :style="{ borderLeft: `4px solid ${alertColor}` }">
            <v-icon :icon="alertIcon" :color="alertColor" size="32"/>
            <div>
                <div class="font-weight-bold text-body-1">{{ headline || notification.title || alertTypeName }}</div>
                <div class="text-caption text-medium-emphasis">{{ alertTypeName }}</div>
            </div>
            <v-spacer/>
            <AlertSeverityChip :alert="notification" size="small"/>
        </div>

        <!-- Quick summary banner -->
        <div v-if="summaryText" class="d-flex align-center ga-2 pa-2 px-3 summary-banner" :style="{ background: alertColor + '15', borderLeft: `3px solid ${alertColor}` }">
            <v-icon :icon="summaryIcon" :color="alertColor" size="18"/>
            <span class="text-body-2 font-weight-bold" :style="{ color: alertColor }">{{ summaryText }}</span>
        </div>

        <!-- Alert Info -->
        <v-table density="compact" class="info-table my-2">
            <tbody>
                <tr>
                    <td class="font-weight-bold" width="140">
                        <v-icon :icon="alertIsPhone ? 'mdi-cellphone' : 'mdi-car'" size="small" class="me-1"/>{{ alertIsPhone ? 'Phone' : 'Vehicle' }}
                    </td>
                    <td>{{ notification.asset || (alertIsPhone ? 'Phone' : 'Vehicle') }}</td>
                </tr>
                <tr v-if="deviceSerial">
                    <td class="font-weight-bold">
                        <v-icon icon="mdi-router-wireless" size="small" class="me-1"/>Device
                    </td>
                    <td>{{ deviceSerial }}</td>
                </tr>
                <tr>
                    <td class="font-weight-bold">
                        <v-icon icon="mdi-clock-outline" size="small" class="me-1"/>Time
                    </td>
                    <td>{{ formatTimestamp(notification.event_timestamp) }}</td>
                </tr>
                <tr v-if="notification.monitoring_config_name">
                    <td class="font-weight-bold">
                        <v-icon icon="mdi-cog" size="small" class="me-1"/>Config
                    </td>
                    <td>{{ notification.monitoring_config_name }}</td>
                </tr>
                <tr v-if="templateLabel">
                    <td class="font-weight-bold">
                        <v-icon icon="mdi-tag" size="small" class="me-1"/>Rule
                    </td>
                    <td>{{ templateLabel }}</td>
                </tr>
            </tbody>
        </v-table>

        <!-- Speed Alert Details -->
        <div v-if="alertType === ALERT_TYPE.SPEED && payload" class="payload-section" style="border-color: #F44336;">
            <div class="payload-title" style="color: #F44336;">
                <v-icon icon="mdi-speedometer" size="small" class="me-1"/>Speed Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="payload.speed_threshold">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-speedometer-slow" size="small" class="me-1" color="grey"/>Speed Limit
                        </td>
                        <td>{{ strOperations().unitifyDistance(payload.speed_threshold) }}</td>
                    </tr>
                    <tr v-if="actualSpeed">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-speedometer" size="small" class="me-1" color="red"/>Actual Speed
                        </td>
                        <td class="text-red font-weight-bold">{{ strOperations().unitifyDistance(actualSpeed) }}</td>
                    </tr>
                    <tr v-if="actualSpeed && payload.speed_threshold">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-arrow-up-bold" size="small" class="me-1" color="red"/>Over By
                        </td>
                        <td class="text-red">{{ strOperations().unitifyDistance(actualSpeed - payload.speed_threshold) }}</td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Geofence Alert Details -->
        <div v-if="alertType === ALERT_TYPE.GEOFENCE && payload" class="payload-section" style="border-color: #FF9800;">
            <div class="payload-title" style="color: #FF9800;">
                <v-icon icon="mdi-map-marker-radius" size="small" class="me-1"/>Geofence Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="geofenceName">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-fence" size="small" class="me-1" color="orange"/>Zone
                        </td>
                        <td>{{ geofenceName }}</td>
                    </tr>
                    <tr v-if="direction">
                        <td class="font-weight-bold">
                            <v-icon :icon="direction === 'enter' ? 'mdi-login' : 'mdi-logout'" size="small" class="me-1"
                                    :color="direction === 'enter' ? 'green' : 'red'"/>
                            Action
                        </td>
                        <td :class="direction === 'enter' ? 'text-green' : 'text-red'" class="font-weight-bold">
                            {{ direction === 'enter' ? 'Entered Zone' : 'Exited Zone' }}
                        </td>
                    </tr>
                    <tr v-if="payload.radius">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-radius-outline" size="small" class="me-1" color="grey"/>Radius
                        </td>
                        <td>{{ payload.radius }} meters</td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Temperature Alert Details -->
        <div v-if="alertType === ALERT_TYPE.TEMPERATURE && payload" class="payload-section" style="border-color: #2196F3;">
            <div class="payload-title" style="color: #2196F3;">
                <v-icon icon="mdi-thermometer" size="small" class="me-1"/>Temperature Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="payload.current_temperature != null">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-thermometer" size="small" class="me-1" color="red"/>Current
                        </td>
                        <td class="font-weight-bold">{{ payload.current_temperature }}&deg;C</td>
                    </tr>
                    <tr v-if="payload.low_threshold_celsius != null">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-thermometer-low" size="small" class="me-1" color="blue"/>Low Threshold
                        </td>
                        <td>{{ payload.low_threshold_celsius }}&deg;C</td>
                    </tr>
                    <tr v-if="payload.high_threshold_celsius != null">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-thermometer-high" size="small" class="me-1" color="red"/>High Threshold
                        </td>
                        <td>{{ payload.high_threshold_celsius }}&deg;C</td>
                    </tr>
                    <tr v-if="payload.current_temperature != null && (payload.low_threshold_celsius != null || payload.high_threshold_celsius != null)">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-alert" size="small" class="me-1" color="orange"/>Status
                        </td>
                        <td class="text-orange font-weight-bold">
                            {{ payload.current_temperature < payload.low_threshold_celsius ? 'Below Low Threshold' :
                               payload.current_temperature > payload.high_threshold_celsius ? 'Above High Threshold' : 'Out of Range' }}
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Light Sensor Alert Details -->
        <div v-if="alertType === ALERT_TYPE.LIGHT && payload" class="payload-section" style="border-color: #FFC107;">
            <div class="payload-title" style="color: #F9A825;">
                <v-icon icon="mdi-lightbulb-on" size="small" class="me-1"/>Light Sensor Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="payload.lux != null">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-brightness-6" size="small" class="me-1" color="amber"/>Light Level
                        </td>
                        <td>{{ payload.lux }} lux</td>
                    </tr>
                    <tr v-if="payload.lux_int_type">
                        <td class="font-weight-bold">
                            <v-icon :icon="payload.lux_int_type === 'light_to_dark' ? 'mdi-weather-night' : 'mdi-white-balance-sunny'"
                                    size="small" class="me-1"
                                    :color="payload.lux_int_type === 'light_to_dark' ? 'indigo' : 'amber'"/>
                            Change Type
                        </td>
                        <td class="font-weight-bold">{{ formatLuxType(payload.lux_int_type) }}</td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Push Button Alert Details -->
        <div v-if="alertType === ALERT_TYPE.PUSH_BUTTON && payload" class="payload-section" style="border-color: #9C27B0;">
            <div class="payload-title" style="color: #9C27B0;">
                <v-icon icon="mdi-gesture-tap-button" size="small" class="me-1"/>Push Button Alert
            </div>
            <v-table density="compact">
                <tbody>
                    <tr>
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-radiobox-marked" size="small" class="me-1" color="purple"/>Status
                        </td>
                        <td class="font-weight-bold">Button Pressed</td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Low Fuel Alert (type 14) -->
        <div v-if="alertType === ALERT_TYPE.FUEL_LOW && payload" class="payload-section" style="border-color: #F97316;">
            <div class="payload-title" style="color: #F97316;">
                <v-icon icon="mdi-gas-station" size="small" class="me-1"/>Low Fuel Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="fuelPercent(payload.fuel_level) != null">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-gas-station" size="small" class="me-1" color="orange"/>Fuel Level
                        </td>
                        <td class="font-weight-bold" style="color: #F97316;">{{ fuelPercent(payload.fuel_level) }}%</td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- SOS Emergency (type 13) -->
        <v-alert v-if="alertType === ALERT_TYPE.SOS" type="error" variant="tonal" class="mx-0 mb-3 rounded"
                 :icon="sosBanner.icon" prominent>
            <div class="font-weight-black text-uppercase" style="letter-spacing: 0.06em;">{{ sosBanner.heading }}</div>
            <div class="text-body-2 mt-1">{{ sosBanner.body }}</div>
        </v-alert>

        <!-- No-Show Alert (type 9) -->
        <div v-if="alertType === ALERT_TYPE.NO_SHOW && payload" class="payload-section" style="border-color: #795548;">
            <div class="payload-title" style="color: #795548;">
                <v-icon icon="mdi-clock-alert-outline" size="small" class="me-1"/>No-Show Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="payload.place_name || payload.geofence_name || payload.location_name || payload.template_label">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-map-marker" size="small" class="me-1" color="brown"/>Expected At
                        </td>
                        <td>{{ payload.place_name || payload.geofence_name || payload.location_name || payload.template_label }}</td>
                    </tr>
                    <tr>
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-alert-circle-outline" size="small" class="me-1" color="brown"/>Status
                        </td>
                        <td class="font-weight-bold" style="color: #795548;">Did Not Arrive</td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Place Alert (type 10) -->
        <div v-if="alertType === ALERT_TYPE.PLACE && payload" class="payload-section" style="border-color: #009688;">
            <div class="payload-title" style="color: #009688;">
                <v-icon icon="mdi-map-marker-check" size="small" class="me-1"/>Place Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="payload.place_name || payload.location_name || payload.template_label">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-map-marker" size="small" class="me-1" color="teal"/>Place
                        </td>
                        <td>{{ payload.place_name || payload.location_name || payload.template_label }}</td>
                    </tr>
                    <tr v-if="direction">
                        <td class="font-weight-bold">
                            <v-icon :icon="direction === 'enter' ? 'mdi-login' : 'mdi-logout'"
                                    size="small" class="me-1"
                                    :color="direction === 'enter' ? 'green' : 'red'"/>
                            Direction
                        </td>
                        <td :class="direction === 'enter' ? 'text-green' : 'text-red'" class="font-weight-bold">
                            {{ direction === 'enter' ? 'Arrived' : 'Left' }}
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Departure / Arrival Alerts (types 11, 12) -->
        <div v-if="(alertType === ALERT_TYPE.DEPARTURE || alertType === ALERT_TYPE.ARRIVAL) && payload" class="payload-section"
             style="border-color: #3F51B5;">
            <div class="payload-title" style="color: #3F51B5;">
                <v-icon :icon="alertType === ALERT_TYPE.DEPARTURE ? 'mdi-map-marker-up' : 'mdi-map-marker-down'"
                        size="small" class="me-1"/>
                {{ alertType === ALERT_TYPE.DEPARTURE ? 'Departure' : 'Arrival' }} Details
            </div>
            <v-table density="compact">
                <tbody>
                    <tr v-if="payload.place_name || payload.location_name || payload.template_label">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-map-marker" size="small" class="me-1" color="indigo"/>Location
                        </td>
                        <td>{{ payload.place_name || payload.location_name || payload.template_label }}</td>
                    </tr>
                    <tr>
                        <td class="font-weight-bold">
                            <v-icon :icon="alertType === ALERT_TYPE.DEPARTURE ? 'mdi-exit-run' : 'mdi-home-import-outline'"
                                    size="small" class="me-1" color="indigo"/>Status
                        </td>
                        <td class="font-weight-bold" style="color: #3F51B5;">
                            {{ alertType === ALERT_TYPE.DEPARTURE ? 'Vehicle Departed' : 'Vehicle Arrived' }}
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- System Alert Details (Battery, Crash, Maintenance) -->
        <div v-if="isSystemAlert && payload" class="payload-section" :style="{ borderColor: systemAlertColor }">
            <div class="payload-title" :style="{ color: systemAlertColor }">
                <v-icon :icon="systemAlertIcon" size="small" class="me-1"/>
                {{ formatSubType(payload.sub_type) }}
            </div>
            <v-table density="compact">
                <tbody>
                    <!-- Battery/Charger alerts. battery1_status/battery2_status
                         SELECT which channel the product reports, they are not
                         "this cell exists" flags, so show the monitored rail
                         first under the SKU's own name. On an OROBDREV23 that
                         is "External Battery" (12V) with "Internal Backup"
                         (the 2.8V supercap) second, not the other way round. -->
                    <template v-if="isBatteryChargerAlert">
                        <tr v-if="primaryBattery">
                            <td class="font-weight-bold" width="140">
                                <v-icon icon="mdi-flash" size="small" class="me-1" color="orange"/>{{ primaryBatteryLabel }}
                            </td>
                            <td>{{ primaryBattery }}</td>
                        </tr>
                        <tr v-if="secondaryVoltage">
                            <td class="font-weight-bold">
                                <v-icon icon="mdi-flash-outline" size="small" class="me-1" color="orange"/>{{ secondaryBatteryLabel }}
                            </td>
                            <td>{{ (secondaryVoltage / 1000).toFixed(3) }}v</td>
                        </tr>
                    </template>

                    <!-- Tamper / power loss. The firmware only reports that
                         primary power went away, never why, so name all three
                         causes instead of accusing. -->
                    <tr v-if="payload.sub_type === SYSTEM_ALERT_SUB_TYPE.DEVICE_UNPLUGGED">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-alert-octagon" size="small" class="me-1" color="deep-orange"/>Status
                        </td>
                        <td class="font-weight-bold text-deep-orange">
                            Lost primary power, may be unplugged, tampered with or the battery is depleted
                        </td>
                    </tr>

                    <tr v-if="payload.sub_type === SYSTEM_ALERT_SUB_TYPE.PUSH_BUTTON_SOS">
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-alarm-light" size="small" class="me-1" color="red"/>Status
                        </td>
                        <td class="font-weight-bold text-red">
                            The panic button on the tracker was pressed
                        </td>
                    </tr>

                    <!-- Maintenance alerts: DTC info -->
                    <template v-if="payload.sub_type === SYSTEM_ALERT_SUB_TYPE.MAINTENANCE_REQUIRED">
                        <tr v-if="payload.pending_dtcs">
                            <td class="font-weight-bold" width="140">
                                <v-icon icon="mdi-alert-circle" size="small" class="me-1" color="orange"/>Pending DTCs
                            </td>
                            <td class="font-weight-bold text-orange">{{ payload.pending_dtcs }}</td>
                        </tr>
                        <tr v-if="payload.indicator_status">
                            <td class="font-weight-bold">
                                <v-icon icon="mdi-engine" size="small" class="me-1" color="amber"/>MIL Lamp
                            </td>
                            <td>{{ payload.indicator_status }}</td>
                        </tr>
                        <tr v-if="payload.red_stop_lamp === 1">
                            <td class="font-weight-bold">
                                <v-icon icon="mdi-alert-octagon" size="small" class="me-1" color="error"/>Red Stop Lamp
                            </td>
                            <td class="font-weight-bold text-error">On</td>
                        </tr>
                        <tr v-if="payload.amber_warning_lamp === 1">
                            <td class="font-weight-bold">
                                <v-icon icon="mdi-alert" size="small" class="me-1" color="amber"/>Amber Warning Lamp
                            </td>
                            <td>On</td>
                        </tr>
                        <tr v-if="payload.protect_lamp === 1">
                            <td class="font-weight-bold">
                                <v-icon icon="mdi-shield-alert" size="small" class="me-1" color="amber"/>Protect Lamp
                            </td>
                            <td>On</td>
                        </tr>
                    </template>

                    <!-- Crash alert -->
                    <template v-if="payload.sub_type === SYSTEM_ALERT_SUB_TYPE.CRASH_DETENTION">
                        <tr>
                            <td class="font-weight-bold" width="140">
                                <v-icon icon="mdi-car-emergency" size="small" class="me-1" color="red"/>Status
                            </td>
                            <td class="font-weight-bold text-red">Crash Event Detected</td>
                        </tr>
                    </template>
                </tbody>
            </v-table>
        </div>

        <!-- DTC Details Section (for maintenance alerts) -->
        <div v-if="showDtcSection" class="payload-section" style="border-color: #FF9800;">
            <div class="payload-title" style="color: #FF9800;">
                <v-icon icon="mdi-car-wrench" size="small" class="me-1"/>Diagnostic Trouble Codes (DTC)
            </div>

            <!-- Loading state -->
            <div v-if="dtcLoading" class="d-flex align-center ga-2 pa-3">
                <v-progress-circular :size="18" :width="2" indeterminate color="orange"/>
                <span class="text-medium-emphasis text-caption">Loading diagnostic codes...</span>
            </div>

            <!-- Error state -->
            <v-alert v-else-if="dtcError" density="compact" type="warning" variant="tonal" class="mb-0">
                {{ dtcError }}
            </v-alert>

            <!-- DTC data -->
            <template v-else-if="dtcData">
                <!-- Summary counts -->
                <div class="d-flex ga-3 mb-2 pa-2" style="background: rgba(255, 152, 0, 0.08); border-radius: 4px;">
                    <div v-if="dtcData.no_of_stored_dtcs != null" class="text-center">
                        <div class="text-h6 text-red font-weight-bold">{{ dtcData.no_of_stored_dtcs || 0 }}</div>
                        <div class="text-caption">Stored</div>
                    </div>
                    <div v-if="dtcData.no_of_pending_dtcs != null" class="text-center">
                        <div class="text-h6 text-orange font-weight-bold">{{ dtcData.no_of_pending_dtcs || 0 }}</div>
                        <div class="text-caption">Pending</div>
                    </div>
                    <div v-if="dtcData.no_of_permanent_dtcs != null" class="text-center">
                        <div class="text-h6 text-deep-orange font-weight-bold">{{ dtcData.no_of_permanent_dtcs || 0 }}</div>
                        <div class="text-caption">Permanent</div>
                    </div>
                    <v-spacer/>
                    <div v-if="dtcData.event_datetime" class="text-caption text-medium-emphasis align-self-center">
                        Reported: {{ formatTimestamp(dtcData.event_datetime) }}
                    </div>
                </div>

                <!-- Individual DTC codes -->
                <template v-if="dtcData.description">
                    <div v-for="(codes, typeName) in dtcData.description" :key="typeName">
                        <div class="text-caption font-weight-bold text-medium-emphasis mb-1 mt-2">{{ typeName }}</div>
                        <div v-for="dtc in codes" :key="dtc.code" class="dtc-code-card mb-2">
                            <div class="d-flex align-center ga-2 mb-1">
                                <v-chip size="small" color="orange" variant="flat" class="font-weight-bold" style="font-family: monospace;">
                                    {{ dtc.code }}
                                </v-chip>
                                <span class="text-body-2 font-weight-bold text-medium-emphasis">{{ dtc.description }}</span>
                            </div>
                            <div v-if="dtc.user_description" class="d-flex align-start ga-1 mb-1 ps-1">
                                <v-icon icon="mdi-information" size="x-small" color="blue" class="mt-1"/>
                                <span class="text-caption text-blue-darken-1">{{ dtc.user_description }}</span>
                            </div>
                            <div v-if="dtc.suggested_fix" class="d-flex align-start ga-1 ps-1">
                                <v-icon icon="mdi-wrench" size="x-small" color="green" class="mt-1"/>
                                <span class="text-caption text-green-darken-1">{{ dtc.suggested_fix }}</span>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- No codes -->
                <div v-else-if="!dtcData.description || Object.keys(dtcData.description).length === 0" class="text-caption text-medium-emphasis pa-2">
                    No diagnostic codes reported
                </div>
            </template>
        </div>

        <!-- Location Section -->
        <div v-if="hasLocation" class="payload-section" style="border-color: #4CAF50;">
            <div class="payload-title" style="color: #4CAF50;">
                <v-icon icon="mdi-map-marker" size="small" class="me-1"/>Reported Location
            </div>
            <v-table density="compact">
                <tbody>
                    <tr>
                        <td class="font-weight-bold" width="140">
                            <v-icon icon="mdi-crosshairs-gps" size="small" class="me-1"/>Coordinates
                        </td>
                        <td class="d-flex align-center ga-1">
                            <span>{{ coords.lat }}, {{ coords.lng }}</span>
                            <v-tooltip location="top" text="Open in Google Maps">
                                <template v-slot:activator="{ props: tp }">
                                    <v-btn :href="`https://www.google.com/maps?q=${coords.lat},${coords.lng}`"
                                           target="_blank" icon="mdi-google-maps" aria-label="Open in Google Maps" size="x-small" variant="text"
                                           color="red" density="compact" v-bind="tp"/>
                                </template>
                            </v-tooltip>
                            <v-tooltip location="top" text="Copy coordinates">
                                <template v-slot:activator="{ props: tp }">
                                    <v-btn icon="mdi-content-copy" aria-label="Copy coordinates" size="x-small" variant="text"
                                           color="medium-emphasis" density="compact" v-bind="tp"
                                           @click="copyCoords"/>
                                </template>
                            </v-tooltip>
                        </td>
                    </tr>
                    <tr v-if="payload.location_name">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-office-building-marker" size="small" class="me-1"/>Location
                        </td>
                        <td>{{ payload.location_name }}</td>
                    </tr>
                    <tr v-if="displayAddress">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-map-search" size="small" class="me-1"/>Address
                        </td>
                        <td>
                            <span>{{ displayAddress }}</span>
                            <v-progress-circular v-if="geocoding" :size="12" :width="2" indeterminate color="grey" class="ms-2"/>
                        </td>
                    </tr>
                    <tr v-else-if="geocoding">
                        <td class="font-weight-bold">
                            <v-icon icon="mdi-map-search" size="small" class="me-1"/>Address
                        </td>
                        <td>
                            <v-progress-circular :size="12" :width="2" indeterminate color="grey" class="me-2"/>
                            <span class="text-medium-emphasis text-caption">Looking up address...</span>
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <!-- Fallback: show payload if no specific type matched and payload exists -->
        <div v-if="showFallback && payload" class="payload-section" style="border-color: #9E9E9E;">
            <div class="payload-title" style="color: #757575;">
                <v-icon icon="mdi-code-json" size="small" class="me-1"/>Raw Data
            </div>
            <pre class="payload-raw">{{ payload }}</pre>
        </div>
    </div>
</template>

<script setup>
import {ALERT_TYPE, SYSTEM_ALERT_TYPES, SYSTEM_ALERT_SUB_TYPE, SYSTEM_ALERTS_TYPE,
    BATTERY_SUB_TYPES, ALERT_TYPE_LABEL, ALERT_SUB_TYPE_META, DEFAULT_ALERT_ICON, subTypeLabel,
    alertHeadline, alertLabel, alertTypeIcon, alertTypeColor, enterOrExitOf, fuelPercent, payloadLatLng} from '@/composables/alertTypes';
import {computed, ref, watch} from "vue";
import {send} from "@/composables/apiClient";
import {mapTiles} from "@/composables/mapConfig";
import {useClipboard} from "@/composables/clipboard";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {strOperations} from "@/composables/globals";
import {formatCompanyDateTime} from "@/composables/useTimezone";
import AlertSeverityChip from "@/components/cards/AlertSeverityChip.vue";

// the types that have their own detail section below, anything else falls back
const KNOWN_SECTION_TYPES = [ALERT_TYPE.GEOFENCE, ALERT_TYPE.SPEED, ALERT_TYPE.LIGHT,
    ALERT_TYPE.PUSH_BUTTON, ALERT_TYPE.TEMPERATURE, ALERT_TYPE.NO_SHOW, ALERT_TYPE.PLACE,
    ALERT_TYPE.DEPARTURE, ALERT_TYPE.ARRIVAL, ALERT_TYPE.SOS, ALERT_TYPE.FUEL_LOW];

const {copyToClipboard} = useClipboard();
const {showSnackbar} = useSnackbarStore();

const props = defineProps({
    notification: {type: Object, required: true},
});

const payload = computed(() => props.notification?.payload || null);
const headline = computed(() => alertHeadline(props.notification));
const alertType = computed(() => {
    const num = props.notification?.alert_type ?? payload.value?.alert_type;
    if (num) return parseInt(num);
    const typeMap = {
        geofence: ALERT_TYPE.GEOFENCE, speed: ALERT_TYPE.SPEED, light: ALERT_TYPE.LIGHT,
        push_button: ALERT_TYPE.PUSH_BUTTON, temperature: ALERT_TYPE.TEMPERATURE,
        system_alerts: ALERT_TYPE.MAINTENANCE, sos: ALERT_TYPE.SOS, fuel: ALERT_TYPE.FUEL_LOW,
    };
    return typeMap[payload.value?.type] || null;
});

// The device-health family (6, 7, 8, 13) is named by its sub_type, so a
// connected charger is not a "Battery Alert" and 6 reads "Maintenance / Check Engine".
const alertTypeName = computed(() => {
    if (isSystemAlert.value || alertType.value === ALERT_TYPE.SOS) {
        return alertLabel({...props.notification, alert_type: alertType.value});
    }
    const label = ALERT_TYPE_LABEL[alertType.value];
    return label ? `${label} Alert` : 'Alert';
});

// Type 13 covers a hardware tamper and a pressed panic button. Only the
// sub_type separates them, so the banner and the header follow it.
const sosSubType = computed(() => {
    if (alertType.value !== ALERT_TYPE.SOS) return null;
    const sub = payload.value?.sub_type ?? props.notification?.sub_type;
    return sub ? String(sub).toLowerCase() : null;
});

const sosBanner = computed(() => {
    if (sosSubType.value === SYSTEM_ALERT_SUB_TYPE.DEVICE_UNPLUGGED) {
        return {
            heading: 'Possible Tampering',
            body: 'The tracker lost primary power. It may be unplugged, tampered with or the battery is depleted.',
            icon: ALERT_SUB_TYPE_META[SYSTEM_ALERT_SUB_TYPE.DEVICE_UNPLUGGED].icon,
            color: ALERT_SUB_TYPE_META[SYSTEM_ALERT_SUB_TYPE.DEVICE_UNPLUGGED].color,
        };
    }
    if (sosSubType.value === SYSTEM_ALERT_SUB_TYPE.PUSH_BUTTON_SOS) {
        return {
            heading: 'Panic Button',
            body: 'The panic button was pressed. Check on the vehicle immediately.',
            icon: ALERT_SUB_TYPE_META[SYSTEM_ALERT_SUB_TYPE.PUSH_BUTTON_SOS].icon,
            color: ALERT_SUB_TYPE_META[SYSTEM_ALERT_SUB_TYPE.PUSH_BUTTON_SOS].color,
        };
    }
    return {
        heading: 'SOS Emergency',
        body: 'Emergency alert triggered. Check on the vehicle immediately.',
        icon: alertTypeIcon(ALERT_TYPE.SOS),
        color: alertTypeColor(ALERT_TYPE.SOS),
    };
});

const isSystemAlert = computed(() => {
    if (!payload.value) return false;
    return payload.value.type === SYSTEM_ALERTS_TYPE || payload.value.sub_type ||
           SYSTEM_ALERT_TYPES.includes(alertType.value);
});

const isBatteryChargerAlert = computed(() => {
    const sub = payload.value?.sub_type;
    return BATTERY_SUB_TYPES.includes(sub);
});

// --- Alert voltage channels ---
//
// battery1_status / battery2_status do not mean "this cell exists", they SELECT
// which channel the product reports and exactly one is ever set. On an Orion OBD
// battery2 is the 12V vehicle rail and voltage1 is the ~2.8V supercap, so reading
// voltage1 blindly showed the supercap as "the battery" and an 11.48V low battery
// alert rendered as 2.76v. Phones instead encode a PERCENT on voltage1
// (percent = voltage1 / 50). Same precedence the live map (DevicesMap) and the
// API's buildBatteryPush already use.
const alertIsPhone = computed(() => payload.value?.is_phone === true || payload.value?.is_phone === 1);
const usesBattery2 = computed(
    () => payload.value?.battery2_status === true && payload.value?.battery1_status !== true
);

const primaryVoltage = computed(() => {
    const p = payload.value;
    if (!p) return null;
    const mv = (!alertIsPhone.value && usesBattery2.value) ? p.voltage2 : p.voltage1;
    return (mv === null || mv === undefined || isNaN(mv)) ? null : Number(mv);
});

const secondaryVoltage = computed(() => {
    const p = payload.value;
    if (!p || alertIsPhone.value) return null;
    const mv = usesBattery2.value ? p.voltage1 : p.voltage2;
    return (mv === null || mv === undefined || isNaN(mv)) ? null : Number(mv);
});

// "12%" on a phone, "11.477v" on hardware.
const primaryBattery = computed(() => {
    const mv = primaryVoltage.value;
    if (mv === null) return null;
    if (alertIsPhone.value) return `${Math.max(0, Math.min(100, Math.round(mv / 50)))}%`;
    return `${(mv / 1000).toFixed(3)}v`;
});

// The SKU names its own channels (product_skus.battery1_label/battery2_label,
// sent on the alert payload). Nullable, not every SKU sets them and alerts raised
// before the API sent them have no such key, so keep a fallback.
const channelLabel = (channel2) => {
    const raw = payload.value?.[channel2 ? 'battery2_label' : 'battery1_label'];
    const s = (raw ?? '').toString().trim();
    return s === '' ? null : s;
};

const primaryBatteryLabel = computed(() => {
    if (alertIsPhone.value) return channelLabel(false) || 'Battery';
    return channelLabel(usesBattery2.value) || 'Battery Voltage';
});

const secondaryBatteryLabel = computed(
    () => channelLabel(!usesBattery2.value) || (usesBattery2.value ? 'Internal Backup' : 'External Voltage')
);

const showFallback = computed(() => {
    return !KNOWN_SECTION_TYPES.includes(alertType.value) && !isSystemAlert.value;
});

const alertIcon = computed(() => {
    if (alertType.value === ALERT_TYPE.SOS) return sosBanner.value.icon;
    if (isSystemAlert.value) return systemAlertIcon.value;
    return alertTypeIcon(alertType.value);
});

const alertColor = computed(() => {
    if (alertType.value === ALERT_TYPE.SOS) return sosBanner.value.color;
    if (isSystemAlert.value) return systemAlertColor.value;
    return alertTypeColor(alertType.value);
});

const systemAlertIcon = computed(() =>
    ALERT_SUB_TYPE_META[payload.value?.sub_type]?.icon || DEFAULT_ALERT_ICON
);

const systemAlertColor = computed(() =>
    ALERT_SUB_TYPE_META[payload.value?.sub_type]?.color || '#673AB7'
);

const actualSpeed = computed(() => payload.value?.current_speed || payload.value?.speed || null);

const deviceSerial = computed(() =>
    payload.value?.device_slno || payload.value?.slno || props.notification?.device_slno || null
);

const geofenceName = computed(() =>
    payload.value?.template_label || props.notification?.template_label || null
);

// enter_or_exit: 1 is entered, any other value is exited.
const direction = computed(() => enterOrExitOf(payload.value));

const geofenceAction = computed(() => {
    if (!direction.value) return null;
    return direction.value === 'enter' ? 'Entered' : 'Exited';
});

const summaryText = computed(() => {
    if (!payload.value) return null;
    if (alertType.value === ALERT_TYPE.SPEED) {
        const speed = actualSpeed.value;
        const limit = payload.value.speed_threshold;
        if (speed && limit) return `Speed: ${strOperations().unitifyDistance(speed)} (Limit: ${strOperations().unitifyDistance(limit)})`;
        if (speed) return `Speed: ${strOperations().unitifyDistance(speed)}`;
        return null;
    }
    if (alertType.value === ALERT_TYPE.GEOFENCE) {
        const action = geofenceAction.value;
        const zone = geofenceName.value;
        if (action && zone) return `${action}: ${zone}`;
        if (action) return action;
        if (zone) return `Zone: ${zone}`;
        return null;
    }
    if (alertType.value === ALERT_TYPE.TEMPERATURE) {
        const temp = payload.value.current_temperature;
        if (temp != null) return `Temperature: ${temp}°C`;
        return null;
    }
    if (alertType.value === ALERT_TYPE.LIGHT) {
        const lux = payload.value.lux;
        const change = formatLuxType(payload.value.lux_int_type);
        if (lux != null && change) return `${change} (${lux} lux)`;
        if (lux != null) return `Light Level: ${lux} lux`;
        return null;
    }
    if (alertType.value === ALERT_TYPE.PUSH_BUTTON) return 'Push Button Pressed';
    if (alertType.value === ALERT_TYPE.FUEL_LOW) {
        const f = fuelPercent(payload.value?.fuel_level);
        return f != null ? `Fuel level: ${f}%` : 'Low fuel';
    }
    if (alertType.value === ALERT_TYPE.SOS) return sosBanner.value.heading;
    if (isSystemAlert.value) {
        const sub = formatSubType(payload.value.sub_type);
        // Quote the MONITORED channel (and a percent for phones), not voltage1,
        // which on an OBD tracker is the supercap rather than the rail the alert
        // is about.
        if (isBatteryChargerAlert.value && primaryBattery.value) {
            return `${sub} - ${primaryBattery.value}`;
        }
        return sub;
    }
    if (alertType.value === ALERT_TYPE.NO_SHOW) {
        const place = payload.value?.place_name || payload.value?.geofence_name
            || payload.value?.location_name || payload.value?.template_label;
        return place ? `Did not arrive at ${place}` : 'Tracker did not arrive at expected location';
    }
    if (alertType.value === ALERT_TYPE.PLACE) {
        const place = payload.value?.place_name || payload.value?.location_name
            || payload.value?.template_label;
        if (place && direction.value) return direction.value === 'enter' ? `Arrived at ${place}` : `Left ${place}`;
        if (place) return `Place event: ${place}`;
        return null;
    }
    if (alertType.value === ALERT_TYPE.DEPARTURE) {
        const place = payload.value?.place_name || payload.value?.location_name
            || payload.value?.template_label;
        return place ? `Departed from ${place}` : 'Vehicle departed';
    }
    if (alertType.value === ALERT_TYPE.ARRIVAL) {
        const place = payload.value?.place_name || payload.value?.location_name
            || payload.value?.template_label;
        return place ? `Arrived at ${place}` : 'Vehicle arrived';
    }
    return null;
});

const summaryIcon = computed(() => {
    const t = alertType.value;
    if (t === ALERT_TYPE.GEOFENCE || t === ALERT_TYPE.PLACE) {
        if (direction.value) return direction.value === 'enter' ? 'mdi-login' : 'mdi-logout';
        return alertTypeIcon(t);
    }
    return alertIcon.value;
});

const templateLabel = computed(() =>
    payload.value?.template_label || props.notification?.template_label || null
);

// A phone SOS carries lat/lng rather than latitude/longitude.
const coords = computed(() => payloadLatLng(payload.value));
const hasLocation = computed(() => coords.value !== null);

// Reverse geocoding for address fallback
const geocodedAddress = ref(null);
const geocoding = ref(false);
let geocodeRequestId = 0;

const displayAddress = computed(() =>
    payload.value?.location || payload.value?.formatted_address || geocodedAddress.value || null
);

const reverseGeocode = (lat, lng) => {
    const requestId = ++geocodeRequestId;
    geocoding.value = true;
    fetch(`${mapTiles.reverseUrl}?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=0`)
        .then(res => res.json())
        .then(data => {
            if (requestId !== geocodeRequestId) return;
            if (data?.display_name) {
                geocodedAddress.value = data.display_name;
            }
        })
        .catch(() => {})
        .finally(() => {
            if (requestId === geocodeRequestId) geocoding.value = false;
        });
};

// Auto-geocode when location has coords but no address
watch(() => payload.value, (p) => {
    geocodedAddress.value = null;
    geocodeRequestId++;
    const c = payloadLatLng(p);
    if (c && !p.location && !p.formatted_address) {
        reverseGeocode(c.lat, c.lng);
    } else {
        geocoding.value = false;
    }
}, {immediate: true});

// DTC fetching for maintenance alerts
const dtcLoading = ref(false);
const dtcError = ref(null);
const dtcData = ref(null);
const showDtcSection = computed(() =>
    payload.value?.sub_type === SYSTEM_ALERT_SUB_TYPE.MAINTENANCE_REQUIRED && props.notification?.asset_id
);

const fetchDtcs = () => {
    const assetId = props.notification?.asset_id || payload.value?.asset_id;
    if (!assetId) return;
    dtcLoading.value = true;
    dtcError.value = null;
    send('get', `assets/${assetId}/dtcs`)
        .then((response) => {
            dtcData.value = response.data.data;
        })
        .catch(() => {
            dtcError.value = 'Could not load diagnostic codes for this asset';
        })
        .finally(() => {
            dtcLoading.value = false;
        });
};

// Auto-fetch DTCs when maintenance alert is shown
watch(showDtcSection, (show) => {
    if (show && !dtcData.value && !dtcLoading.value) fetchDtcs();
}, {immediate: true});

const formatTimestamp = (ts) => {
    if (!ts) return '';
    // Company timezone: alert + DTC times must match how the server localizes
    // alert times and the fleet app's display.
    return formatCompanyDateTime(ts);
};

const formatSubType = (subType) => {
    if (!subType) return 'System Alert';
    return subTypeLabel(subType);
};

const formatLuxType = (type) => {
    if (!type) return '';
    const types = {
        'light_to_dark': 'Light to Dark',
        'dark_to_light': 'Dark to Light',
    };
    return types[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const copyCoords = () => {
    if (coords.value) {
        copyToClipboard(`${coords.value.lat}, ${coords.value.lng}`);
    }
};
</script>

<style scoped>
.alert-payload-details {
    padding: 8px;
}
.alert-header {
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 6px;
}
.summary-banner {
    border-radius: 4px;
    margin-top: 8px;
    font-weight: 600;
}
.info-table {
    background: transparent;
}
.info-table td {
    padding: 6px 12px !important;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08) !important;
}
.payload-section {
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
    border-left-width: 4px;
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 10px;
}
.payload-title {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
}
.payload-section td {
    padding: 4px 8px !important;
}
.payload-raw {
    color: #ccc;
    padding: 8px;
    background: #2d2d2d;
    white-space: pre-wrap;
    border-radius: 4px;
    font-size: 12px;
    max-height: 300px;
    overflow-y: auto;
}
.dtc-code-card {
    background: rgba(var(--v-theme-on-surface), 0.03);
    border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    border-radius: 6px;
    padding: 8px 10px;
}
</style>
